import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import WeatherComponent from "../../components/widgets/WeatherWidget";
import ThreeViewerWithTimeline from "../../components/viewer/viewer3d/ThreeViewerWithTimeline";
import ApprovalWidget from "../../components/widgets/ApprovalWidget";
import DDayCounter from "../../components/widgets/DDayCounter";
import MilestoneSchedule from "../../components/widgets/MilestoneSchedule";
import NoticeWidget from "../../components/widgets/NoticeWidget";
import SafetyStatusStrip from "../../components/widgets/SafetyStatusStrip";
import ConstPhotoWidget from "../../components/widgets/ConstructionPhotoWidget";
import TodoWidget from "../../components/widgets/TodoWidget";
import WidgetCard from "../../components/widgets/WidgetCard";
import {
  Factory,
  Truck,
  AlertTriangle,
  Settings,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import {
  getProjectSummary,
  getProjectDetail,
  getProjectWeather,
} from "../../api/project";

const DEFAULT_SETTINGS = {
  showSummaryCards: true,
  showDDayAndMilestone: true,
  showWeather: true,
  showApproval: true,
  showFourDPreview: true,
  showConstPhotos: true,
  showNotice: true,
};

function ProjectDashboard() {
  const { id: projectId } = useParams();

  const [projectname, setProjectname] = useState("-");
  const [weatherData, setWeatherData] = useState(null);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [dashboardPage, setDashboardPage] = useState(0);

  const [gltfFiles, setGltfFiles] = useState([]);
  const [selectedGltf, setSelectedGltf] = useState(null);

  const [hasFourDModule, setHasFourDModule] = useState(false);

  /* ===============================
     ✅ PAGE 정의 (4D 모듈 여부 기반)
     =============================== */
  const PAGES = useMemo(() => {
    return hasFourDModule
      ? [
        { key: "overview", title: "프로젝트 진행 현황 (Overview)" },
        { key: "4d", title: "4D 시뮬레이션 (Simulation)" },
        { key: "safety", title: "스마트 안전 관제 (CCTV / IoT)" },
      ]
      : [
        { key: "overview", title: "프로젝트 진행 현황 (Overview)" },
        { key: "safety", title: "스마트 안전 관제 (CCTV / IoT)" },
      ];
  }, [hasFourDModule]);

  const TOTAL_PAGES = PAGES.length;

  /* ===============================
     프로젝트 모듈 정보
     =============================== */
  useEffect(() => {
    async function fetchModuleInfo() {
      if (!projectId) return;
      try {
        const res = await getProjectDetail(projectId);
        const modules = res.data.modules_ordered || [];
        setHasFourDModule(modules.includes("fourd"));
      } catch (err) {
        console.error("모듈 정보 로드 실패", err);
        setHasFourDModule(false);
      }
    }
    fetchModuleInfo();
  }, [projectId]);

  /* ===============================
     프로젝트 요약
     =============================== */
  useEffect(() => {
    async function loadSummary() {
      try {
        const res = await getProjectSummary();
        setProjectname(res.project.name);
      } catch (err) {
        console.error("프로젝트 요약 실패", err);
      }
    }
    loadSummary();
  }, [projectId]);

  /* ===============================
     날씨
     =============================== */
  useEffect(() => {
    if (!projectId) return;
    getProjectWeather(projectId).then((data) => {
      if (data?.forecast?.length) {
        setWeatherData(data.forecast[0].values);
      }
    });
  }, [projectId]);

  /* ===============================
     4D BIM glTF 로드
     =============================== */
  useEffect(() => {
    const loadFourD = async () => {
      if (!projectId || !hasFourDModule) {
        setGltfFiles([]);
        setSelectedGltf(null);
        return;
      }

      try {
        const { getBimTree } = await import("../../api/bim");
        const res = await getBimTree(projectId);
        const { folders = [], files = [] } = res.data || {};

        const collect = (list, acc = []) => {
          list.forEach((f) => {
            if (f.files?.length) acc.push(...f.files);
            if (f.children?.length) collect(f.children, acc);
          });
          return acc;
        };

        const all = [...files, ...collect(folders)].map((f) => ({
          id: f.id,
          name: f.name,
          file: f.gltf_file,
        }));

        setGltfFiles(all);
        setSelectedGltf(all[0] || null);
      } catch (err) {
        console.error("4D glTF 로드 실패", err);
      }
    };

    loadFourD();
  }, [projectId, hasFourDModule]);

  /* ===============================
     대시보드 설정
     =============================== */
  useEffect(() => {
    if (!projectId) return;
    try {
      const raw = localStorage.getItem(`dashboardSettings_${projectId}`);
      setSettings(raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS);
    } catch {
      setSettings(DEFAULT_SETTINGS);
    }
  }, [projectId]);

  const updateSetting = (key) => (e) => {
    const next = { ...settings, [key]: e.target.checked };
    setSettings(next);
    localStorage.setItem(`dashboardSettings_${projectId}`, JSON.stringify(next));
  };

  const nextPage = () =>
    setDashboardPage((p) => (p + 1) % TOTAL_PAGES);
  const prevPage = () =>
    setDashboardPage((p) => (p - 1 + TOTAL_PAGES) % TOTAL_PAGES);

  const currentPage = PAGES[dashboardPage]?.key;

  return (
    <div className="px-0 py-6 space-y-6">
      {/* ================= HEADER ================= */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {projectname}
              <span className="ml-2 text-sm text-gray-500 font-normal">대시보드</span>
            </h2>
            <p className="text-xs text-blue-600 font-medium">
              {PAGES[dashboardPage]?.title}
            </p>
          </div>

          <div className="flex items-center bg-white border rounded-lg p-1 shadow-sm">
            <button onClick={prevPage} className="p-1.5 hover:bg-gray-100 rounded">
              <ChevronLeft size={18} />
            </button>
            <div className="flex gap-1 mx-2">
              {PAGES.map((_, i) => (
                <div
                  key={i}
                  className={`h-2 rounded-full transition-all ${dashboardPage === i
                    ? "bg-blue-500 w-4"
                    : "bg-gray-300 w-2"
                    }`}
                />
              ))}
            </div>
            <button onClick={nextPage} className="p-1.5 hover:bg-gray-100 rounded">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* <button
          onClick={() => setSettingsOpen((v) => !v)}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          <Settings size={20} />
        </button> */}
      </div>

      {/* ================= SETTINGS ================= */}
      {settingsOpen && (
        <div className="border rounded-xl bg-white p-4 space-y-2">
          <p className="font-semibold text-sm">표시할 위젯</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2">
            {Object.keys(DEFAULT_SETTINGS)
              .filter(
                (k) => hasFourDModule || k !== "showFourDPreview"
              )
              .map((key) => (
                <CheckboxRow
                  key={key}
                  label={key.replace("show", "")}
                  checked={settings[key]}
                  onChange={updateSetting(key)}
                />
              ))}
          </div>
        </div>
      )}

      {/* ================= PAGE: OVERVIEW ================= */}
      {currentPage === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-1 md:col-span-2">
              <SafetyStatusStrip projectId={projectId} />
            </div>

            {settings.showSummaryCards && (
              <>
                <SummaryCard
                  title="금일 작업"
                  value="12건"
                  subtitle="진행 8 / 완료 4"
                  icon={<Factory className="text-blue-600" />}
                />
                <SummaryCard
                  title="장비 투입"
                  value="8대"
                  subtitle="크레인 3 / 굴삭기 5"
                  icon={<Truck className="text-orange-600" />}
                />
                <SummaryCard
                  title="부적합 사항"
                  value="2건"
                  subtitle="조치요망"
                  icon={<AlertTriangle className="text-red-600" />}
                />
              </>
            )}
          </div>

          {settings.showDDayAndMilestone && (
            <div className="grid gap-4 grid-cols-1 xl:grid-cols-[1fr_3fr_1fr]">
              <DDayCounter />
              <MilestoneSchedule />
              {settings.showWeather && <WeatherComponent />}
            </div>

          )}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {settings.showConstPhotos && (
              <div className="lg:col-span-3">
                <ConstPhotoWidget />
              </div>
            )}
            {settings.showApproval && (
              <ApprovalWidget />
            )}
            <div className="flex flex-col gap-4">
              <TodoWidget />
              {settings.showNotice && <NoticeWidget />}
            </div>
          </div>
        </div>
      )}

      {/* ================= PAGE: 4D ================= */}
      {currentPage === "4d" && hasFourDModule && settings.showFourDPreview && (
        <div className="h-[calc(100vh-200px)] min-h-[600px]">
          {selectedGltf ? (
            <div className="h-full border rounded-xl bg-white flex flex-col">
              <ThreeViewerWithTimeline
                gltfUrl={selectedGltf.file}
                weatherData={weatherData}
              />
              {gltfFiles.length > 1 && (
                <div className="border-t p-3 flex gap-2 flex-wrap bg-slate-50">
                  {gltfFiles.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setSelectedGltf(f)}
                      className={`px-3 py-1.5 rounded-full text-sm border ${selectedGltf.id === f.id
                        ? "bg-blue-50 border-blue-400 text-blue-700 font-bold"
                        : "bg-white border-slate-200"
                        }`}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              등록된 4D 모델이 없습니다.
            </div>
          )}
        </div>
      )}

      {/* ================= PAGE: SAFETY ================= */}
      {currentPage === "safety" && (
        <div className="text-gray-400 h-[400px] flex items-center justify-center">
          스마트 안전 관제 영역 (CCTV / IoT)
        </div>
      )}
    </div>
  );
}

/* ================= SUB COMPONENTS ================= */

function CheckboxRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="text-sm text-slate-600">{label}</span>
    </label>
  );
}

function SummaryCard({ title, value, subtitle, icon }) {
  return (
    <WidgetCard title={title}>
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-full">
          {icon}
        </div>
        <div>
          <p className="text-xl font-bold">{value}</p>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>
    </WidgetCard>
  );
}

export default ProjectDashboard;
